import { use, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBrowserSession } from "@softmax/web-features/session";
import {
  QueryViewerContext,
  useDiscardUnavailableData,
} from "@softmax/web-features/data";
import type { AccountProfileDraft } from "@softmax/product-ux/account";
import { currentTosVersion } from "@/lib/tos";
import { accountProfileQueryOptions } from "../account.queries";
import {
  accountAvatarMutationOptions,
  accountProfileMutationOptions,
} from "../account.mutations";
import { isUncertainWrite } from "../write-failure";

export function useAccountSettings() {
  const viewerId = use(QueryViewerContext);
  const { refresh: updateSession, kind: sessionKind } = useBrowserSession();
  const client = useQueryClient();
  const writing = useRef(false);
  const options = accountProfileQueryOptions({ viewerId });
  const query = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: query.error,
  });
  const [draft, setDraft] = useState<AccountProfileDraft | null>(null);
  const save = useMutation({
    ...accountProfileMutationOptions({ queryClient: client, viewerId }),
    onSettled: () => {
      writing.current = false;
    },
  });
  const avatar = useMutation({
    ...accountAvatarMutationOptions({ queryClient: client, viewerId }),
    onSettled: () => {
      writing.current = false;
    },
  });
  const saveUncertain = !!save.error && isUncertainWrite(save.error);
  const avatarUncertain = !!avatar.error && isUncertainWrite(avatar.error);
  const blocked =
    sessionKind !== "authenticated" ||
    save.isPending ||
    avatar.isPending ||
    saveUncertain ||
    avatarUncertain;

  async function refreshAccount() {
    if (query.isFetching) return;

    const before = query.dataUpdatedAt;
    const result = await query.refetch();

    if (result.isSuccess && result.dataUpdatedAt > before) {
      if (saveUncertain) save.reset();

      if (avatarUncertain) avatar.reset();

      void updateSession();
    }
  }

  function changeAvatar(file: File | null) {
    if (blocked || writing.current) return;

    writing.current = true;
    avatar.mutate(file, { onSuccess: () => void updateSession() });
  }

  function changeProfile(profile: AccountProfileDraft) {
    setDraft(profile);

    if (!saveUncertain) save.reset();
  }

  function saveProfile({
    profile,
    tosVersion,
  }: {
    readonly profile: AccountProfileDraft;
    readonly tosVersion: string | null;
  }) {
    if (blocked || writing.current) return;

    writing.current = true;
    save.mutate(
      { ...profile, hasAcceptedTos: tosVersion === currentTosVersion },
      {
        onSuccess: () => {
          setDraft(null);
          void updateSession();
        },
      },
    );
  }

  return {
    viewerId,
    query,
    unavailable,
    draft,
    save,
    avatar,
    avatarUncertain,
    blocked,
    refreshAccount,
    changeAvatar,
    changeProfile,
    saveProfile,
  };
}
